import Button from '@superfunnel/ui-core/v2/Button';
import Input from '@superfunnel/ui-core/v2/Input';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { Trash2Icon } from 'lucide-react';
import { useState } from 'react';
import { uid } from 'uid';

export interface GuidelineItem {
  id: string;
  title: string;
  value: string;
}

interface GuidelinesManagerProps {
  guidelines: GuidelineItem[];
  onChange: (guidelines: GuidelineItem[]) => void;
  titlePlaceholder?: string;
  valuePlaceholder?: string;
}

export const GuidelinesManager = ({
  guidelines,
  onChange,
  titlePlaceholder = 'Guideline title',
  valuePlaceholder = 'Guideline description...',
}: GuidelinesManagerProps) => {
  const [titleTouched, setTitleTouched] = useState<boolean>(false);
  const [contentTouched, setContentTouched] = useState<boolean>(false);

  const addGuideline = () => {
    onChange([
      ...guidelines,
      {
        id: uid(8),
        title: '',
        value: '',
      },
    ]);
  };

  const updateGuideline = (
    id: string,
    field: 'title' | 'value',
    newValue: string,
  ) => {
    onChange(
      guidelines.map((item) =>
        item.id === id ? { ...item, [field]: newValue } : item,
      ),
    );
  };

  const deleteGuideline = (id: string) => {
    onChange(guidelines.filter((item) => item.id !== id));
  };

  return (
    <div className="space-y-3">
      {guidelines.map((guideline) => (
        <div
          key={guideline.id}
          className="border-base-200 rounded-2lg space-y-2 border p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <Input
              value={guideline.title}
              onChange={(e) =>
                updateGuideline(guideline.id, 'title', e.target.value)
              }
              onBlur={() => setTitleTouched(true)}
              placeholder={titlePlaceholder}
              className="flex-1"
              validation={
                !guideline.title?.trim().length && titleTouched
                  ? { status: 'error', message: 'Title is required' }
                  : undefined
              }
              autoFocus={false}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => deleteGuideline(guideline.id)}
              className="text-error-600 hover:text-error-700 hover:bg-error-50"
            >
              <Trash2Icon className="size-4" />
            </Button>
          </div>
          <TextArea
            value={guideline.value}
            onChange={(e) =>
              updateGuideline(guideline.id, 'value', e.target.value)
            }
            onBlur={() => setContentTouched(true)}
            validation={
              !guideline.value?.trim().length && contentTouched
                ? { status: 'error', message: 'Description is required' }
                : undefined
            }
            placeholder={valuePlaceholder}
            rows={3}
            className="max-h-36"
          />
        </div>
      ))}
      <Button
        variant="outline"
        onClick={addGuideline}
        className="w-full"
        type="button"
      >
        + Add Guideline
      </Button>
    </div>
  );
};
